import { useCallback, useMemo, useState } from "react";
import { isBoostActive, isFeaturedActive, type ListingLike } from "@/lib/listings";
import { haversineKm, type GeoPoint } from "@/lib/geo";

export const DISTANCE_OPTIONS = [
  { km: 1, label: "1 km" },
  { km: 3, label: "3 km" },
  { km: 5, label: "5 km" },
  { km: 10, label: "10 km" },
  { km: 25, label: "25 km" },
  { km: 50, label: "50 km" },
];

export interface SearchFilters {
  category: string;
  subcategory: string;
  state: string;
  city: string;
  neighborhood: string;
  q: string;
  maxKm: number | null;
  sort: string;
  online: boolean;
  service: string[];
  attributes: Record<string, string[]>;
}

const DEFAULT_FILTERS: SearchFilters = {
  category: "",
  subcategory: "",
  state: "",
  city: "",
  neighborhood: "",
  q: "",
  maxKm: null,
  sort: "default",
  online: false,
  service: [],
  attributes: {},
};

interface CategoryLike {
  slug: string;
  name: string;
}

interface EnrichedListing extends ListingLike {
  category_name: string;
  category_slug?: string;
  online?: boolean;
  name?: string;
}

export function enrichListings(
  listings: ListingLike[],
  categories: CategoryLike[],
  userLocation: GeoPoint | null,
  filters: SearchFilters
): EnrichedListing[] {
  let out: EnrichedListing[] = listings.map((l) => {
    const el = l as EnrichedListing;
    return {
      ...el,
      category_name:
        categories.find((c) => c.slug === el.category_slug)?.name ||
        (el.category_slug as string),
    };
  });

  if (userLocation && filters.maxKm != null) {
    const maxKm = filters.maxKm;
    out = out.filter((l) => {
      if (l.lat == null || l.lng == null) return false;
      const km = haversineKm(userLocation, { lat: l.lat, lng: l.lng });
      return km != null && km <= maxKm;
    });
  }

  if (filters.online) {
    out = out.filter((l) => l.online || l.is_online);
  }

out.sort((a, b) => {
     if (filters.sort === "distance" && userLocation) {
       const da = a.lat != null && a.lng != null ? haversineKm(userLocation, { lat: a.lat as number, lng: a.lng as number }) : Infinity;
       const db = b.lat != null && b.lng != null ? haversineKm(userLocation, { lat: b.lat as number, lng: b.lng as number }) : Infinity;
       if (da === Infinity && db === Infinity) {
         const ta = new Date((a.user_last_active_at || a.created_at || 0) as string).getTime();
         const tb = new Date((b.user_last_active_at || b.created_at || 0) as string).getTime();
         return tb - ta;
       }
       return (da || 0) - (db || 0);
     }
     if (filters.sort === "recent") {
       return new Date(b.created_at as string || 0).getTime() - new Date(a.created_at as string || 0).getTime();
     }
     if (filters.sort === "name") {
       return (a.name || "").localeCompare(b.name || "");
     }
     // default: boost → featured → distance-if-loc → recent
     const aBoost = isBoostActive(a);
     const bBoost = isBoostActive(b);
     if (aBoost !== bBoost)
       return (bBoost ? 1 : 0) - (aBoost ? 1 : 0);
     const aFeat = isFeaturedActive(a);
     const bFeat = isFeaturedActive(b);
     if (aFeat !== bFeat)
       return (bFeat ? 1 : 0) - (aFeat ? 1 : 0);
     if (userLocation) {
       const da = a.lat != null && a.lng != null ? haversineKm(userLocation, { lat: a.lat as number, lng: a.lng as number }) : Infinity;
       const db = b.lat != null && b.lng != null ? haversineKm(userLocation, { lat: b.lat as number, lng: b.lng as number }) : Infinity;
       if (da === Infinity && db === Infinity) {
         const ta = new Date((a.user_last_active_at || a.created_at || 0) as string).getTime();
         const tb = new Date((b.user_last_active_at || b.created_at || 0) as string).getTime();
         return tb - ta;
       }
       return (da || 0) - (db || 0);
     }
     return new Date(b.created_at as string || 0).getTime() - new Date(a.created_at as string || 0).getTime();
   });

  return out;
}

interface BuildApiParamsOpts {
  includeAttributes?: boolean;
  includeSort?: boolean;
  limit?: number | null;
}

export function buildApiParams(
  filters: SearchFilters,
  userLocation?: GeoPoint | null,
  opts: BuildApiParamsOpts = {}
): Record<string, unknown> {
  const { includeAttributes = true, includeSort = false, limit = null } = opts;
  const params: Record<string, unknown> = {};
  if (filters.category) params.category = filters.category;
  if (filters.subcategory) params.subcategory = filters.subcategory;
  if (filters.state) params.state = filters.state;
  if (filters.city) params.city = filters.city;
  if (filters.neighborhood) params.neighborhood = filters.neighborhood;
  if (filters.q && filters.q.trim()) params.q = filters.q.trim();
  if (filters.online) params.online_only = true;
  if (filters.service.length) params.service = filters.service.join(",");
  if (userLocation?.lat != null && userLocation?.lng != null) {
    params.lat = userLocation.lat;
    params.lng = userLocation.lng;
    if (filters.maxKm != null) params.max_km = filters.maxKm;
  }
  if (includeSort && filters.sort !== "default") params.sort = filters.sort;
  if (limit) params.limit = limit;
  if (includeAttributes) {
    Object.entries(filters.attributes).forEach(([id, vals]) => {
      if (vals && vals.length > 0) params[`attr_${id}`] = vals.join(",");
    });
  }
  return params;
}

interface UseSearchFiltersOptions {
  initialFilters?: Partial<SearchFilters>;
  onFiltersChange?: ((next: SearchFilters) => void) | null;
  includeAttributes?: boolean;
  includeSort?: boolean;
  limit?: number | null;
}

interface UseSearchFiltersReturn {
  filters: SearchFilters;
  setCategory: (v: string) => void;
  setSubcategory: (v: string) => void;
  setState: (v: string) => void;
  setCity: (v: string) => void;
  setNeighborhood: (v: string) => void;
  setQ: (v: string) => void;
  setMaxKm: (v: number | null) => void;
  setSort: (v: string) => void;
  setOnline: (v: boolean) => void;
  setService: (v: string | string[]) => void;
  setAttributes: (id: string, vals: string[]) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  apiParams: Record<string, unknown>;
  DISTANCE_OPTIONS: typeof DISTANCE_OPTIONS;
}

export default function useSearchFilters({
  initialFilters = {},
  onFiltersChange = null,
  includeAttributes = true,
  includeSort = false,
  limit = null,
}: UseSearchFiltersOptions = {}): UseSearchFiltersReturn {
  const merged: SearchFilters = { ...DEFAULT_FILTERS, ...initialFilters };
  const [filters, setFiltersState] = useState<SearchFilters>(merged);

  const updateFilters = useCallback(
    (patch: Partial<SearchFilters> | ((prev: SearchFilters) => SearchFilters)) => {
      setFiltersState((prev) => {
        const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
        if (onFiltersChange) onFiltersChange(next);
        return next;
      });
    },
    [onFiltersChange]
  );

  const setCategory = useCallback((v: string) => updateFilters({ category: v, subcategory: "" }), [updateFilters]);
  const setSubcategory = useCallback((v: string) => updateFilters({ subcategory: v }), [updateFilters]);
  const setState = useCallback((v: string) => updateFilters({ state: v, city: "", neighborhood: "" }), [updateFilters]);
  const setCity = useCallback((v: string) => updateFilters({ city: v, neighborhood: "" }), [updateFilters]);
  const setNeighborhood = useCallback((v: string) => updateFilters({ neighborhood: v }), [updateFilters]);
  const setQ = useCallback((v: string) => updateFilters({ q: v }), [updateFilters]);
  const setMaxKm = useCallback((v: number | null) => updateFilters({ maxKm: v }), [updateFilters]);
  const setSort = useCallback((v: string) => updateFilters({ sort: v }), [updateFilters]);
  const setOnline = useCallback((v: boolean) => updateFilters({ online: v }), [updateFilters]);
  const setService = useCallback((v: string | string[]) => updateFilters({ service: Array.isArray(v) ? v : [v] }), [updateFilters]);
  const setAttributes = useCallback((id: string, vals: string[]) => 
    updateFilters((prev) => ({ 
      ...prev, 
      attributes: { ...prev.attributes, [id]: vals } 
    })), [updateFilters]);

  const clearFilters = useCallback(() => {
    // Preserve context filters — category, state, city — so that clicking
    // "Limpar filtros" does not wipe the user's browsing context. They are
    // only changed when the user manually picks a different value or uses
    // the location ("Usar minha localização") flow, which sets state/city.
    const next: SearchFilters = {
      ...DEFAULT_FILTERS,
      category: filters.category || "",
      state: filters.state || "",
      city: filters.city || "",
    };
    if (onFiltersChange) onFiltersChange(next);
    setFiltersState(next);
  }, [filters.category, filters.state, filters.city, onFiltersChange]);

  const hasActiveFilters = useMemo(() => {
    const f = filters;
    return !!(
      f.category ||
      f.subcategory ||
      f.state ||
      f.city ||
      f.neighborhood ||
      f.q ||
      f.online ||
      f.maxKm != null ||
      f.service.length ||
      f.sort !== "default" ||
      Object.values(f.attributes).some((v) => v && v.length > 0)
    );
  }, [filters]);

  const apiParams = useMemo(
    () =>
      buildApiParams(filters, null, {
        includeAttributes,
        includeSort,
        limit,
      }),
    [filters, includeAttributes, includeSort, limit]
  );

  const returnValue = useMemo(
    () => ({
      filters,
      setCategory,
      setSubcategory,
      setState,
      setCity,
      setNeighborhood,
      setQ,
      setMaxKm,
      setSort,
      setOnline,
      setService,
      setAttributes,
      clearFilters,
      hasActiveFilters,
      apiParams,
      DISTANCE_OPTIONS,
    }),
    [
      filters,
      setCategory,
      setSubcategory,
      setState,
      setCity,
      setNeighborhood,
      setQ,
      setMaxKm,
      setSort,
      setOnline,
      setService,
      setAttributes,
      clearFilters,
      hasActiveFilters,
      apiParams,
    ]
  );

  return returnValue;
}